import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiConnectorGet, apiConnectorPost } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";
import toast from "react-hot-toast";

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  pageBg:      "#000000",
  cardBg:      "#131920",
  inputBg:     "#0d1117",
  border:      "1px solid rgba(0,229,255,0.18)",
  borderFocus: "1px solid rgba(0,229,255,0.55)",
  cyan:        "#00e5ff",
  gold:        "#f5a623",
  green:       "#00b578",
  greenBtn:    "linear-gradient(135deg, #00b578 0%, #00e676 100%)",
  red:         "#ff5252",
  textPrimary: "#ffffff",
  textMuted:   "#9ca3af",
  textDim:     "#6b7280",
};

const WALLETS = [
  { key: "registration_bonus_wallet",    label: "Register Bonus", icon: "🎁", field: "tr03_reg_bonus" },
  { key: "from_topup_wallet", label: "Trade Wallet",   icon: "⬆️", field: "tr03_topup_wallet" },
  { key: "income_wallet",   label: "Income Wallet",  icon: "💰", field: "tr03_inc_wallet" },
];

function fmtDate(str) {
  if (!str) return "-";
  const d = new Date(str);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── History ───────────────────────────────────────────────────────────────────
function HistoryView() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["self_transfer_history", page],
    queryFn: () =>
      apiConnectorPost(endpoint.get_report_details, {
        search:          "",
        start_date:      "",
        end_date:        "",
        page,
        count:           10,
        sub_label:       "FUND WALLET",
        main_label:      "IN",
        is_global:       false,
        onlyP2PTransfer: false,
      }),
    keepPreviousData: true,
  });

  const rows       = data?.data?.result?.data || [];
  const totalPages = Math.max(1, data?.data?.result?.total_pages || 1);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{
          width: "34px", height: "34px", borderRadius: "50%",
          border: "3px solid rgba(0,229,255,0.15)", borderTopColor: C.cyan,
          animation: "spin 0.8s linear infinite", margin: "0 auto",
        }} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ fontSize: "34px", marginBottom: "12px" }}>↩</div>
        <p style={{ color: C.textDim, fontSize: "14px" }}>No self-transfer history yet</p>
      </div>
    );
  }

  return (
    <div>
      {rows.map((row, i) => (
        <div key={row?.tr07_trans_id || i} style={{
          background: C.inputBg, borderRadius: "12px",
          border: C.border, padding: "14px", marginBottom: "10px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "7px" }}>
            <span style={{ color: C.textDim, fontSize: "11px" }}>
              #{row?.tr07_trans_id || "-"}
            </span>
            <span style={{ color: C.cyan, fontWeight: "700", fontSize: "15px" }}>
              ${parseFloat(row?.tr07_tr_amount || 0).toFixed(3)} USDT
            </span>
          </div>
          <p style={{ color: "rgba(160,220,235,0.75)", fontSize: "11px", marginBottom: "8px" }}>
            {fmtDate(row?.tr07_created_at)}
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
            <span style={{
              background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.3)",
              borderRadius: "6px", padding: "3px 10px",
              color: C.cyan, fontSize: "11px", fontWeight: "700",
            }}>
              {row?.tr07_sub_label || "Fund Wallet"}
            </span>
            <span style={{
              background: "rgba(0,181,120,0.12)", border: "1px solid rgba(0,181,120,0.3)",
              borderRadius: "6px", padding: "3px 10px",
              color: C.green, fontSize: "11px", fontWeight: "700",
            }}>
              ✓ Success
            </span>
          </div>
          {row?.tr07_description && (
            <p style={{
              color: "rgba(190,235,245,0.8)", fontSize: "11px",
              background: "rgba(0,229,255,0.04)", borderRadius: "6px",
              padding: "6px 8px", border: "1px solid rgba(0,229,255,0.12)",
            }}>
              {row.tr07_description}
            </p>
          )}
        </div>
      ))}

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginTop: "16px" }}>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          style={{
            background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.2)",
            borderRadius: "8px", color: page === 1 ? "#374151" : C.cyan,
            fontSize: "13px", padding: "7px 16px",
            cursor: page === 1 ? "not-allowed" : "pointer",
          }}
        >← Prev</button>
        <span style={{ color: C.textDim, fontSize: "12px" }}>{page} / {totalPages}</span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          style={{
            background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.2)",
            borderRadius: "8px", color: page === totalPages ? "#374151" : C.cyan,
            fontSize: "13px", padding: "7px 16px",
            cursor: page === totalPages ? "not-allowed" : "pointer",
          }}
        >Next →</button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SelfTransfer() {
  const queryClient               = useQueryClient();
  const [view, setView]           = useState("form");
  const [selectedWallet, setSelectedWallet] = useState("from_topup_wallet");
  const [amount, setAmount]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: profileData } = useQuery({
    queryKey: ["member_details_self"],
    queryFn:  () => apiConnectorGet(endpoint.member_details),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const profile     = profileData?.data?.result?.[0] || {};
  const fundBalance = parseFloat(profile?.tr03_fund_wallet || 0);
  const ownCustId   = profile?.lgn_cust_id || profile?.tr03_cust_id || "";

  const activeWallet   = WALLETS.find((w) => w.key === selectedWallet);
  const sourceBalance  = parseFloat(profile?.[activeWallet?.field] || 0);
  const isOverBalance  = amount !== "" && Number(amount) > sourceBalance;

  const validate = () => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return false;
    }
    if (sourceBalance < Number(amount)) {
      toast.error(`Insufficient balance in ${activeWallet?.label}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    const res = await apiConnectorPost(endpoint.member_fund_transfer, {
      pkg_amount:  amount,
      pkg_id:      1,
      to_cust_id:  ownCustId,
      wallet_type: selectedWallet,
    });
    setSubmitting(false);
    if (res?.data?.success) {
      toast.success(res.data.message || "Transfer successful!");
      setAmount("");
      queryClient.invalidateQueries({ queryKey: ["member_details_self"] });
      queryClient.invalidateQueries({ queryKey: ["self_transfer_history"] });
    } else {
      toast.error(res?.data?.message || "Transfer failed. Please try again.");
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.pageBg,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "20px 16px 100px",
    }}>
      <div style={{
        width: "100%", maxWidth: "420px", background: C.cardBg,
        borderRadius: "20px", border: "1px solid rgba(0,229,255,0.14)",
        boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        position: "relative", overflow: "hidden",
      }}>

        {/* Decorative accent strip */}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: "3px",
          background: `linear-gradient(to bottom, ${C.cyan}, ${C.green})`,
        }} />

        {/* Corner accent */}
        <div style={{
          position: "absolute", top: 0, right: 0, width: "72px", height: "72px",
          borderTop: "2px solid rgba(0,229,255,0.15)",
          borderRight: "2px solid rgba(0,229,255,0.15)",
          borderRadius: "0 20px 0 0",
        }} />

        <div style={{ padding: "28px 24px 28px 28px", position: "relative", zIndex: 1 }}>

          {/* ── Header ── */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
                <span style={{
                  width: "10px", height: "10px", borderRadius: "50%",
                  background: C.cyan, boxShadow: `0 0 8px ${C.cyan}`,
                  flexShrink: 0, animation: "pulse 2s infinite",
                }} />
                <span style={{ fontSize: "20px", fontWeight: "800", color: C.cyan, letterSpacing: "-0.3px" }}>
                  Self Transfer
                </span>
              </div>
              <p style={{ color: C.textDim, fontSize: "13px", marginLeft: "20px" }}>
                Move funds into your Spot Wallet
              </p>
            </div>
            <button
              onClick={() => setView(view === "form" ? "history" : "form")}
              style={{
                flexShrink: 0, marginLeft: "12px",
                background: view === "history" ? "rgba(0,229,255,0.14)" : "rgba(0,229,255,0.06)",
                border: "1px solid rgba(0,229,255,0.3)",
                borderRadius: "10px", color: C.cyan,
                fontSize: "12px", fontWeight: "600",
                padding: "7px 13px", cursor: "pointer",
                display: "flex", alignItems: "center", gap: "5px",
                whiteSpace: "nowrap",
              }}
            >
              {view === "form" ? <>📋 History</> : <>← Back</>}
            </button>
          </div>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "22px" }}>
            <div style={{ flex: 1, height: "1px", background: "rgba(0,229,255,0.12)" }} />
            <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: C.cyan, boxShadow: `0 0 5px ${C.cyan}` }} />
            <div style={{ flex: 1, height: "1px", background: "rgba(0,229,255,0.12)" }} />
          </div>

          {/* ── Body ── */}
          {view === "history" ? (
            <HistoryView />
          ) : (
            <>
              {/* Fund Wallet (destination) */}
              <label style={{ color: C.textMuted, fontSize: "12px", fontWeight: "600", display: "block", marginBottom: "8px" }}>
                Destination — Spot Wallet
              </label>
              <div style={{
                padding: "13px 16px", background: C.inputBg,
                borderRadius: "12px", border: "1px solid rgba(0,229,255,0.2)",
                marginBottom: "20px", display: "flex",
                alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "16px" }}>🏦</span>
                  <span style={{ color: C.textMuted, fontSize: "12px" }}>Spot Wallet</span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={{ color: C.cyan, fontWeight: "700", fontSize: "16px" }}>
                    ${fundBalance.toFixed(2)}
                  </span>
                  <span style={{ color: C.textDim, fontSize: "10px", marginLeft: "4px" }}>USDT</span>
                </div>
              </div>

              {/* Source Wallet selector */}
              <label style={{ color: C.textMuted, fontSize: "12px", fontWeight: "600", display: "block", marginBottom: "10px" }}>
                Select Source Wallet
              </label>

              {/* Register Bonus — full width */}
              {/* <WalletCard
                wallet={WALLETS[0]}
                balance={parseFloat(profile?.[WALLETS[0].field] || 0)}
                selected={selectedWallet === WALLETS[0].key}
                onSelect={() => setSelectedWallet(WALLETS[0].key)}
                fullWidth
              /> */}

              {/* Topup + Income — side by side */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "20px" }}>
                {WALLETS.slice(1).map((w) => (
                  <WalletCard
                    key={w.key}
                    wallet={w}
                    balance={parseFloat(profile?.[w.field] || 0)}
                    selected={selectedWallet === w.key}
                    onSelect={() => setSelectedWallet(w.key)}
                  />
                ))}
              </div>

              {/* Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "18px" }}>
                <div style={{ flex: 1, height: "1px", background: "rgba(0,229,255,0.10)" }} />
                <span style={{ color: C.textDim, fontSize: "10px", letterSpacing: "2px" }}>ENTER AMOUNT</span>
                <div style={{ flex: 1, height: "1px", background: "rgba(0,229,255,0.10)" }} />
              </div>

              {/* Amount input */}
              <label style={{ color: C.textMuted, fontSize: "12px", fontWeight: "600", display: "block", marginBottom: "7px" }}>
                Amount (USDT) — Available: ${sourceBalance.toFixed(2)}
              </label>
              <div style={{ position: "relative", marginBottom: isOverBalance ? "6px" : "20px" }}>
                <input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  min="0"
                  onChange={(e) => setAmount(e.target.value)}
                  style={{
                    width: "100%", padding: "15px 70px 15px 16px", background: C.inputBg,
                    border: isOverBalance ? "1px solid rgba(255,82,82,0.6)" : C.border,
                    borderRadius: "12px", color: C.textPrimary,
                    fontSize: "18px", fontWeight: "600", outline: "none",
                    boxSizing: "border-box",
                    fontFamily: "inherit", transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => { e.target.style.border = isOverBalance ? "1px solid rgba(255,82,82,0.8)" : C.borderFocus; }}
                  onBlur={(e)  => { e.target.style.border = isOverBalance ? "1px solid rgba(255,82,82,0.6)" : C.border; }}
                />
                <button
                  type="button"
                  onClick={() => setAmount(sourceBalance.toFixed(2))}
                  style={{
                    position: "absolute", right: "10px", top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(0,229,255,0.12)",
                    border: "1px solid rgba(0,229,255,0.35)",
                    borderRadius: "7px", color: C.cyan,
                    fontSize: "11px", fontWeight: "700",
                    padding: "5px 10px", cursor: "pointer",
                    letterSpacing: "0.05em",
                  }}
                >MAX</button>
              </div>
              {isOverBalance && (
                <p style={{
                  color: C.red, fontSize: "11px", marginBottom: "16px",
                  paddingLeft: "4px", display: "flex", alignItems: "center", gap: "5px",
                }}>
                  <span>✕</span> Amount exceeds available balance
                </p>
              )}

              {/* Submit */}
              <button
                onClick={() => { if (validate()) setShowConfirm(true); }}
                disabled={submitting || isOverBalance}
                style={{
                  width: "100%", padding: "17px",
                  background: isOverBalance
                    ? "rgba(255,82,82,0.15)"
                    : submitting ? "rgba(0,181,120,0.35)" : C.greenBtn,
                  border: isOverBalance ? "1px solid rgba(255,82,82,0.35)" : "none",
                  borderRadius: "12px", color: isOverBalance ? C.red : "#fff",
                  fontSize: "16px", fontWeight: "700",
                  cursor: (submitting || isOverBalance) ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  boxShadow: (submitting || isOverBalance) ? "none" : "0 4px 18px rgba(0,181,120,0.3)",
                  letterSpacing: "0.3px",
                }}
              >
                {submitting ? (
                  <>
                    <span style={{
                      width: "16px", height: "16px", borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff",
                      display: "inline-block", animation: "spin 0.7s linear infinite",
                    }} />
                    Processing...
                  </>
                ) : (
                  <>↩ Transfer to Spot Wallet</>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Confirm Modal ── */}
      {showConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
          backdropFilter: "blur(8px)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999, padding: "20px",
        }}>
          <div style={{
            background: C.cardBg, borderRadius: "20px",
            border: "1px solid rgba(0,229,255,0.3)",
            boxShadow: "0 0 40px rgba(0,229,255,0.1)",
            padding: "32px 24px", textAlign: "center",
            maxWidth: "320px", width: "100%",
          }}>
            <div style={{
              width: "60px", height: "60px", borderRadius: "50%",
              background: "rgba(0,229,255,0.1)", border: `2px solid ${C.cyan}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 18px", fontSize: "24px",
              boxShadow: "0 0 16px rgba(0,229,255,0.2)",
            }}>↩</div>
            <h3 style={{ color: C.textPrimary, fontSize: "18px", fontWeight: "700", marginBottom: "10px" }}>
              Confirm Transfer
            </h3>
            <div style={{
              background: C.inputBg, borderRadius: "12px", border: C.border,
              padding: "14px", marginBottom: "20px", textAlign: "left",
            }}>
              {[
                { label: "Amount",  value: `$${Number(amount).toFixed(2)} USDT`, color: C.cyan },
                { label: "From",    value: activeWallet?.label, color: C.gold },
                { label: "To",      value: "Fund Wallet",        color: C.green },
                { label: "Customer ID", value: ownCustId },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", alignItems: "center" }}>
                  <span style={{ color: C.textDim, fontSize: "12px" }}>{label}</span>
                  <span style={{ color: color || C.textPrimary, fontSize: "13px", fontWeight: "600" }}>{value}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  flex: 1, padding: "13px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "12px", color: C.textMuted,
                  fontSize: "14px", fontWeight: "600", cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={handleSubmit}
                style={{
                  flex: 1, padding: "13px", background: C.greenBtn,
                  border: "none", borderRadius: "12px", color: "#fff",
                  fontSize: "14px", fontWeight: "700", cursor: "pointer",
                  boxShadow: "0 4px 16px rgba(0,181,120,0.3)",
                }}
              >Confirm</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 6px #00e5ff; opacity: 1; }
          50%       { box-shadow: 0 0 14px #00e5ff; opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}

// ── Wallet selector card ──────────────────────────────────────────────────────
function WalletCard({ wallet, balance, selected, onSelect, fullWidth }) {
  return (
    <button
      onClick={onSelect}
      style={{
        display: "flex", alignItems: "center", gap: "10px",
        padding: "13px 14px", borderRadius: "12px", cursor: "pointer",
        width: fullWidth ? "100%" : undefined,
        marginBottom: fullWidth ? "10px" : undefined,
        background: selected
          ? "rgba(0,229,255,0.1)"
          : "rgba(13,17,23,0.8)",
        border: selected
          ? "1px solid rgba(0,229,255,0.5)"
          : "1px solid rgba(0,229,255,0.12)",
        transition: "all 0.2s",
        boxShadow: selected ? "0 0 12px rgba(0,229,255,0.12)" : "none",
        textAlign: "left",
      }}
    >
      <span style={{ fontSize: "18px", flexShrink: 0 }}>{wallet.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          color: selected ? "#00e5ff" : "#9ca3af",
          fontSize: "10px", fontWeight: "600",
          letterSpacing: "0.05em", marginBottom: "2px",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {wallet.label}
        </p>
        <p style={{
          color: selected ? "#ffffff" : "#9ca3af",
          fontSize: "13px", fontWeight: "700",
          fontFamily: "monospace",
        }}>
          ${balance.toFixed(2)}
        </p>
      </div>
      {selected && (
        <span style={{
          width: "8px", height: "8px", borderRadius: "50%",
          background: "#00e5ff", flexShrink: 0,
          boxShadow: "0 0 6px #00e5ff",
        }} />
      )}
    </button>
  );
}
